import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { PremiumDetails, PlanDetailsTable } from './components';
import { BenefitPlanDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { PlanInfoResponseDto } from 'enrollment/queries/usePlanInformationQuery/utils';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { usePlanInfoQuery } from 'enrollment/queries/usePlanInformationQuery';
import './PlanDetailsDrawer.css';
import { ToggleBooleanState } from 'components/ToggleBooleanState';
import { PsButtonTuner } from 'stencilSetup';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import { CategoryCoverageDto } from 'enrollment/queries/useBenefitCategoryQuery/types';

interface PlanDetailsDrawerProps {
  plan: CategoryCoverageDto | BenefitPlanDto;
  orgPlanYearId?: number;
  subscriberPremium?: string;
  category?: string;
  planInfoData?: PlanInfoResponseDto;
  isLoadingPlanInfo?: boolean;
  isFetchingPlanInfo?: boolean;
  hideEmptyCost?: boolean;
}

export const PlanDetailsDrawer = ({
  plan,
  orgPlanYearId,
  subscriberPremium,
  category,
  planInfoData,
  isLoadingPlanInfo,
  isFetchingPlanInfo,
  hideEmptyCost = false,
}: PlanDetailsDrawerProps) => {
  const { t } = useTranslation();
  const planInfoProps = { planInfoData, isLoadingPlanInfo, isFetchingPlanInfo };
  const enrollmentContext = useSelector(selectEnrollmentContext);
  const orgPlanId =
    'req_org_plan_id' in plan ? plan.req_org_plan_id : plan.plan_id;
  const [isDrawerOpen, setIsDrawerOpen] = ToggleBooleanState(false);

  const {
    planInfoData: queryData,
    isLoadingPlanInfo: isQueryLoading,
    isFetchingPlanInfo: isQueryFetching,
  } = usePlanInfoQuery({
    orgPlanYearId: orgPlanYearId,
    enrollmentContext: enrollmentContext,
    orgPlanId: orgPlanId,
    enabled:
      Boolean(orgPlanId > 0 && orgPlanYearId) &&
      !isLoadingPlanInfo &&
      !isFetchingPlanInfo &&
      planInfoData === undefined,
  });

  // Determine the source of your data and loading states
  const planData =
    !isLoadingPlanInfo && !isFetchingPlanInfo ? queryData : planInfoData;
  const isLoading = isLoadingPlanInfo || isQueryLoading;
  const isFetching = isFetchingPlanInfo || isQueryFetching;

  const drawerContentDetails = planData?.data?.content_details;

  return (
    <>
      <PsButtonTuner
        buttonId={`planDetailsDrawer${orgPlanId}-trigger`}
        label={t('common:enrollment:viewPlanDetails')}
        level="tertiary"
        extraClass="show-underline"
        onClick={() => setIsDrawerOpen(true)}
        otherData={[
          {
            attribute: 'aria-describedby',
            value: `plan-${orgPlanId}`,
          },
        ]}
      />
      <OrbitDrawer
        drawerId={`planDetailsDrawer${orgPlanId}`}
        heading={'plan_name' in plan ? plan.plan_name : plan.req_org_plan_name}
        dialogLarge={true}
        isOpen={isDrawerOpen}
        handleClose={setIsDrawerOpen}
        hTagImg={
          plan.carrier_logo_url && plan.carrier_logo_url !== null
            ? plan.carrier_logo_url
            : undefined
        }
        hTagImgAlt={
          'req_carrier_name' in plan
            ? plan.req_carrier_name
            : plan.carrier || t('defaultLogoAltText')
        }
      >
        <div className="container-lg mt-2">
          <PremiumDetails
            plan={plan}
            subscriberPremium={subscriberPremium}
            category={category}
            isLoading={isLoading}
            isFetching={isFetching}
            planData={planData}
            hideEmptyCost={hideEmptyCost}
            {...planInfoProps}
          />
          {drawerContentDetails && (
            <PlanDetailsTable contentDetails={drawerContentDetails} />
          )}
        </div>
      </OrbitDrawer>
    </>
  );
};
